/**
 * The geometry behind the signature pad.
 *
 * Everything here is pure and synchronous, for the same reason `imageCropHelper` is: jsdom
 * has no layout engine and no canvas backend, so `getBoundingClientRect` reads zeros,
 * `getContext` returns null and `toDataURL` is not implemented. Pointer maths tested
 * through the DOM would be tested against a canvas that cannot draw. Kept as arithmetic,
 * it can be tested as arithmetic.
 *
 * <p>**The pad exports its whole surface, uncropped.** Trimming to the ink was the obvious
 * thing and is wrong here: the exported PNG is also what gets drawn back when the wizard
 * steps away from the consent page and returns, and a cropped image cannot be put back where
 * it came from without remembering where that was -- which is exactly the state a remount
 * destroys. Redrawing a full-surface export is instead an identity, at any size the pad
 * happens to be. The cost is that somebody who signs small in the corner gets a small
 * signature on the document, which is what happens on paper too.
 */

export interface Point {
    x: number;
    y: number;
}

/** The part of a DOMRect this needs, so a test can pass four numbers. */
export interface Bounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface Size {
    width: number;
    height: number;
}

/**
 * A pointer's page position, in the canvas's own pixels.
 *
 * The two differ by more than the offset: the backing store is deliberately larger than
 * the CSS box, so the drawing is still sharp on the document it ends up in.
 *
 * <p>Returns the origin for a zero-sized box rather than dividing by it. That is not a
 * theoretical case -- it is every render under jsdom, where there is no layout engine and
 * every rect reads zero, and the alternative is `Infinity` and `NaN` propagating into
 * canvas calls.
 */
export const canvasPoint = (client: Point, box: Bounds, canvas: Size): Point => {
    if (box.width === 0 || box.height === 0) {
        return { x: 0, y: 0 };
    }

    return {
        x: (client.x - box.left) * (canvas.width / box.width),
        y: (client.y - box.top) * (canvas.height / box.height),
    };
};

/**
 * The point halfway between two, which is what turns a polyline into a smooth stroke:
 * each segment is a quadratic curve between consecutive midpoints, using the sampled point
 * between them as the control. Drawing straight to each sampled point instead leaves
 * visible corners wherever the pointer was moving quickly, which on a signature reads as a
 * shaky hand rather than a fast one.
 */
export const midpoint = (a: Point, b: Point): Point => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
});

/** Strips the prefix a canvas puts on, leaving what the API takes. */
export const toBarePngBase64 = (dataUrl: string): string =>
    dataUrl.replace(/^data:image\/png;base64,/, '');

/** The inverse, for handing a stored signature back to an `Image` to redraw. */
export const toPngDataUrl = (base64: string): string => `data:image/png;base64,${base64}`;
